import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Box, Checkbox, Popper, Stack } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  DateCalendar,
  DatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { StyleSettings } from "../../styles/StyleSettings";

interface SnCheckPeriodBoxProps {
  // "Y, 20250101, 20250131" 형태 (체크값, 시작일, 종료일)
  defaultValue?: any;
  onValueChanged?: (check: string, dt_start: string, dt_end: string) => void;
  isReadOnly?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
}

const SnCheckPeriodBox = React.memo(
  ({
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    inputRef,
    isSearchPanel = false,
    isFormPanel = false,
  }: SnCheckPeriodBoxProps) => {
    const parseValue = (value: any) => {
      const v: string[] = value ? String(value).split(",") : [];
      return {
        check: (v[0] ?? "").trim().toUpperCase() === "Y",
        start: (v[1] ?? "").trim(),
        end: (v[2] ?? "").trim(),
      };
    };

    const initial = parseValue(defaultValue);

    // check 세팅
    const [checkValue, setCheckValue] = useState<boolean>(initial.check);

    // period 세팅
    const [periodValue, setPeriodValue] = useState<
      [Dayjs | null, Dayjs | null]
    >([
      dayjs(initial.start || new Date()),
      dayjs(initial.end || new Date()),
    ]);

    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const firstTextFieldRef = useRef<HTMLElement | null>(null);
    const secondRef = useRef<HTMLDivElement | null>(null);

    // 체크박스가 꺼져 있으면 기간 입력은 비활성화한다.
    const isPeriodDisabled = isReadOnly || !checkValue;

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue !== undefined) {
          const parsed = parseValue(defaultValue);
          setCheckValue(parsed.check);
          setPeriodValue([
            dayjs(parsed.start || new Date()),
            dayjs(parsed.end || new Date()),
          ]);
        } else {
          setCheckValue(false);
          setPeriodValue([dayjs(new Date()), dayjs(new Date())]);
        }
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    const getPeriodText = (
      startDate: Dayjs | null,
      endDate: Dayjs | null
    ) => {
      let start: string = "";
      let end: string = "";
      if (startDate !== null && startDate.isValid())
        start = startDate.format("YYYYMMDD");
      if (endDate !== null && endDate.isValid())
        end = endDate.format("YYYYMMDD");
      return { start, end };
    };

    const handleCheckChange = (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setCheckValue(checked);
      if (!checked) setIsPopperOpen(false);

      if (onValueChanged) {
        const date = getPeriodText(periodValue[0], periodValue[1]);
        onValueChanged(checked ? "Y" : "N", date.start, date.end);
      }
    };

    const handleOpen = () => {
      if (!isPeriodDisabled) {
        setIsPopperOpen(true);
      }
    };

    const handleClose = () => {
      setIsPopperOpen(false);
      secondRef.current?.focus();
    };

    const emitChange = (startDate: Dayjs | null, endDate: Dayjs | null) => {
      setPeriodValue([startDate, endDate]);
      const date = getPeriodText(startDate, endDate);
      if (onValueChanged) {
        onValueChanged(checkValue ? "Y" : "N", date.start, date.end);
      }
    };

    // 텍스트 입력: 기존과 동일하게 반대편 날짜를 건드리지 않음
    const handleStartDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      emitChange(newValue, periodValue[1]);
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      emitChange(periodValue[0], newValue);
      handleClose();
    };

    // 달력 클릭: 시작일이 종료일보다 뒤면 종료일을 시작일과 같은 날로 맞춤
    const handleStartCalendarChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      const endDate =
        periodValue[1] && newValue.isAfter(periodValue[1], "day")
          ? newValue
          : periodValue[1];
      emitChange(newValue, endDate);
    };

    // 달력 클릭: 종료일이 시작일보다 앞이면 시작일을 종료일과 같은 날로 맞춤
    const handleEndCalendarChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      const startDate =
        periodValue[0] && newValue.isBefore(periodValue[0], "day")
          ? newValue
          : periodValue[0];
      emitChange(startDate, newValue);
      handleClose();
    };

    // 날짜 더블클릭 시 Popper 닫기
    const handleDateDoubleClick = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      handleClose();
    };

    // 외부 클릭 감지
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          firstTextFieldRef.current &&
          !firstTextFieldRef.current.contains(event.target as Node) &&
          !document
            .getElementById("calendar-popper")
            ?.contains(event.target as Node)
        ) {
          handleClose();
        }
      };

      if (isPopperOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isPopperOpen]);

    return (
      <div>
        <Stack direction="row" alignItems="center">
          <Checkbox
            inputRef={inputRef}
            checked={checkValue}
            disabled={isReadOnly}
            onChange={handleCheckChange}
            sx={{
              height: StyleSettings.control_height,
              marginRight: "5px",
              "& .MuiSvgIcon-root": {
                fontSize: 22,
              },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                flex: 1,
              }}
            >
              <div style={{ width: "100%" }}>
                <DatePicker
                  value={periodValue[0]}
                  format={"YYYY/MM/DD"}
                  onChange={handleStartDateChange}
                  disabled={isPeriodDisabled}
                  slotProps={{
                    textField: {
                      onClick: handleOpen,
                      inputRef: firstTextFieldRef,
                      fullWidth: true,
                      sx: {
                        "& .MuiInputBase-root": {
                          height: StyleSettings.control_height,
                          fontSize: StyleSettings.font,
                          "&.Mui-disabled": {
                            backgroundColor: "#f5f5f5",
                          },
                        },
                        backgroundColor: "white",
                      },
                      InputProps: {
                        endAdornment: null, // 달력 아이콘 제거
                      },
                    },
                  }}
                />
              </div>
              <Box> – </Box>
              <div
                onKeyDown={(e) => {
                  if (e.key === "Tab") {
                    handleClose();
                  }
                }}
                style={{ width: "100%" }}
              >
                <DatePicker
                  value={periodValue[1]}
                  format={"YYYY/MM/DD"}
                  onChange={handleEndDateChange}
                  disabled={isPeriodDisabled}
                  slotProps={{
                    textField: {
                      onClick: handleOpen,
                      fullWidth: true,
                      inputRef: secondRef,
                      sx: {
                        "& .MuiInputBase-root": {
                          height: StyleSettings.control_height,
                          fontSize: StyleSettings.font,
                          "&.Mui-disabled": {
                            backgroundColor: "#f5f5f5",
                          },
                        },
                        backgroundColor: "white",
                      },
                      InputProps: {
                        endAdornment: null, // 달력 아이콘 제거
                      },
                    },
                  }}
                />
              </div>
            </Box>
            <Popper
              id="calendar-popper"
              open={isPopperOpen}
              anchorEl={firstTextFieldRef.current} // 항상 첫 번째 DatePicker 기준으로 고정
              placement="bottom-start"
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, -8],
                  },
                },
              ]}
              sx={{
                boxShadow:
                  "0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)",
                borderRadius: "8px",
                backgroundColor: "white",
                zIndex: 1500,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  p: 2,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  "& .MuiDateCalendar-root": {
                    height: "auto",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    marginTop: 0,
                  },
                }}
              >
                <DateCalendar
                  value={periodValue[0]}
                  onChange={handleStartCalendarChange}
                  sx={{
                    width: 300,
                    "& .MuiPickersSlideTransition-root": {
                      minHeight: "205px",
                    },
                  }}
                  slotProps={{
                    day: {
                      onDoubleClickCapture: (event) =>
                        handleDateDoubleClick(event),
                    },
                  }}
                />
                <DateCalendar
                  value={periodValue[1]}
                  onChange={handleEndCalendarChange}
                  sx={{
                    width: 300,
                    "& .MuiPickersSlideTransition-root": {
                      minHeight: "205px",
                    },
                  }}
                />
              </Box>
            </Popper>
          </LocalizationProvider>
        </Stack>
      </div>
    );
  }
);

export default SnCheckPeriodBox;
